import {createSlice} from '@reduxjs/toolkit';
import {
    getUpcomingEventsData,
    resetFilters,
    updateFilters,
} from 'pages/event-management/store/event-schedule-actions';
import {upcomingEventsAdapter} from 'pages/event-management/store/event-schedule-adapters';
import {getEventFilterOptions} from 'pages/event-management/store/past-events-list-actions';
import {EVENT_STATUS_BADGE} from 'pages/event-management/utils/constants';
import dayjs from 'utils/dayjs';

import {displayDateFormat} from 'utils/date-time';

/** @namespace Events.Actions */

const now = dayjs();

/**
 *
 * @typedef {object} TEventScheduleSliceInitialState
 * @property {object} filters
 * @property {string} filters.fromDate - The start date for the events in the format MM/DD/YYYY.  Defaults to the current date
 * @property {string} filters.toDate - The end date for the events in the format MM/DD/YYYY.  Defaults to the end of the current month
 */
export const initialState = {
    appliedFilters: {
        fromDate: now.format(displayDateFormat),
        toDate: now.add(90, 'day').format(displayDateFormat),
        destinationIds: [],
        eventStatusIds: [],
        eventTypeIds: [],
        numFiltersApplied: 0,
    },
    filters: {
        fromDate: now.format(displayDateFormat),
        toDate: now.add(90, 'day').format(displayDateFormat),
        destinationOptions: [],
        eventStatusOptions: Object.values(EVENT_STATUS_BADGE).map((status) => ({
            selected: false,
            label: status.label,
            value: status.id,
            id: status.id,
        })),
        eventTypeOptions: [],
        isLoading: false,
    },
    data: {
        events: upcomingEventsAdapter.getInitialState({
            isLoading: false,
        }),
        meta: {
            nextPage: null,
            page: 1,
            pages: 1,
            perPage: 25,
            total: 25,
        },
    },
};

const eventScheduleSlice = createSlice({
    name: 'events/eventSchedule',
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(updateFilters, (state, action) => {
                state.appliedFilters = action.payload;
            })
            .addCase(resetFilters, (state) => {
                state.appliedFilters = initialState.appliedFilters;
            })
            .addCase(getEventFilterOptions.pending, (state) => {
                state.filters.isLoading = true;
            })
            .addCase(getEventFilterOptions.fulfilled, (state, {payload}) => {
                const {eventTypeIds, venueIds} = payload;

                state.filters = {
                    ...state.filters,
                    destinationOptions: venueIds, // Todo: rename venueIds to destinationOptions
                    eventTypeOptions: eventTypeIds, // Todo: rename eventTypeIds to eventTypeOptions
                    isLoading: false,
                };
            })
            .addCase(getUpcomingEventsData.fulfilled, (state, {payload}) => {
                const {data, meta} = payload;

                upcomingEventsAdapter.setAll(state.data.events, data);
                state.data.events.isLoading = false;

                state.data.meta = meta;
            })
            .addCase(getUpcomingEventsData.pending, (state) => {
                state.data.events.isLoading = true;
            })
            .addCase(getUpcomingEventsData.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }

                state.data.events.isLoading = false;
            });
    },
});

export default eventScheduleSlice.reducer;
